"use client";

import { useState } from "react";
import { usePathname } from "next/navigation";
import { Share2, Check } from "lucide-react";
import { useMessages } from "@/lib/i18n/client";
import messages from "./ShareButton.i18n";
import { track } from "@/lib/tracking/track";
import type { ShareContent } from "@/lib/tracking/events";

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL ?? "https://ciudad-viva.cl";

interface Props {
  title: string;
  contentType: ShareContent;
  // Slug del evento; los panoramas públicos van sin ID (el token es privado).
  itemId?: string;
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
      <path d="M12.001 2C6.478 2 2 6.477 2 12c0 1.986.583 3.837 1.588 5.393L2 22l4.735-1.556A9.953 9.953 0 0 0 12.001 22C17.523 22 22 17.523 22 12S17.523 2 12.001 2zm0 18.164a8.14 8.14 0 0 1-4.15-1.135l-.297-.176-3.096 1.018 1.033-3.038-.193-.31A8.14 8.14 0 0 1 3.836 12c0-4.505 3.66-8.164 8.165-8.164S20.166 7.495 20.166 12 16.506 20.164 12.001 20.164z" />
    </svg>
  );
}

export function ShareButton({ title, contentType, itemId }: Props) {
  // usePathname() en vez de window.location.href para el link de WhatsApp:
  // se renderiza en el markup (no dentro de un handler), así que necesita
  // ser igual en server y cliente para no romper la hidratación.
  const pathname = usePathname();
  const [copied, setCopied] = useState(false);
  const t = useMessages(messages);

  const handleShare = async () => {
    const url = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        track("share", { method: "native", content_type: contentType, item_id: itemId });
      } catch {
        // usuario canceló el diálogo nativo — no hacer nada
      }
      return;
    }

    await navigator.clipboard.writeText(url);
    track("share", { method: "copy_link", content_type: contentType, item_id: itemId });
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(`${title} — ${SITE_URL}${pathname}`)}`;

  return (
    <div className="flex items-center gap-4">
      <a
        href={whatsappHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => track("share", { method: "whatsapp", content_type: contentType, item_id: itemId })}
        className="inline-flex items-center gap-2 text-muted-foreground transition-colors hover:text-[#25D366]"
      >
        <WhatsAppIcon className="h-4 w-4" />
        <span className="text-sm">WhatsApp</span>
      </a>

      <button
        onClick={handleShare}
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        {copied ? (
          <>
            <Check className="h-4 w-4 text-primary" />
            <span className="text-primary text-sm">{t.copied}</span>
          </>
        ) : (
          <>
            <Share2 className="h-4 w-4" />
            <span className="text-sm">{t.share}</span>
          </>
        )}
      </button>
    </div>
  );
}
